import type { ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'

import { ExplanationItem } from './ExplanationItem'
import { TalkList } from './TalkList'
import { talkItemList } from './talkLists'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/enlayout'
import { CompanyLinks } from '@/components/enlayouts/Company/CompanyLinks'
import { Head } from '@/components/enlayouts/Head'

export const PhilosophyPage = () => {
  const explanationItemList = [
    {
      mainTitle: 'Purpose',
      text: <>Enrich every aspect of people&apos;s lives through all we do.</>,
    },
    {
      mainTitle: 'Vision',
      text: <>Revolutionize &quot;real commerce&quot; around the globe with technology and practical savvy.</>,
    },
    {
      mainTitle: 'Value',
      text: (
        <>
          ・The power to distribute goods with our highly efficient network of stores<br />
          ・The ability to fully leverage data and IoT
        </>
      ),
    },
  ]

  return (
    <>
      <Head
        title="Philosophy"
        description="Our group's purpose, vision, and values."
        ogp={{ type: 'article', url: 'en/company/philosophy/' }}
      />
      <PhilosophyMainWrapper>
        <PhilosophyMain>
          <SectionTitle
            enTitleText="Philosophy"
            subTitleText=""
            color="normal"
          />
          <Philosophy>
            <h3>Purpose, Vision & Value</h3>
            <PhilosophyTriangle>
              <ImageWrapper>
                <img
                  src="/images/Philosophy_EN.svg"
                  alt="TRIAL Holdingsのパーパス・ビジョン・バリュー"
                />
              </ImageWrapper>
              <Explanation>
                {explanationItemList.map(({ mainTitle, text }) => (
                  <ExplanationItem
                    key={mainTitle}
                    mainTitle={mainTitle}
                    text={text}
                  />
                ))}
              </Explanation>
            </PhilosophyTriangle>
          </Philosophy>
          <SpecialTalk>
            <h3>
              Special Talk
            </h3>
            <NameWrapper>
              <Name data-left>
                <p>Director<br /> Ryota Ishibashi</p>
              </Name>
              <Name data-right>
                <p>Director<br /> Hiro Nagata</p>
              </Name>
            </NameWrapper>
            <Spacer h={{ sp: 60, pc: 70 }} />
            <img src="/images/philosophy-talk.webp" alt="PhilosophyTalk画像" />
            <Spacer h={{ sp: 34, pc: 60 }} />
          </SpecialTalk>
          <TalkMain>
            <TalkLead>
              <h3>How our Purpose, Vision and Value are created. TRIAL Holdings.<br/> ~Thoughts behind our philosophy~</h3>
              <p>
              &quot;Purpose&quot; for corporate management is attracting attention. It represents the value to the society while clarifying the importance of each company.<br />
              Having clear &quot;Vision&quot; and &quot;Value&quot; unifies the organization.

              </p>
              <p>
                The &quot;Purpose, Vision, and Value&quot; at TRIAL Holdings represents the passion of each of executive directors which emerged through deep r discussions.
                <br />
                Ryota Ishibashi, Director, and Hiro Nagata, Director, talk about the secret story behind the scenes.
              </p>
            </TalkLead>
            <Spacer h={{ sp: 56, pc: 110 }} />
            <Talk>
              {talkItemList.map(({ talkTitle, talkList }) => (
                <TalkList
                  key={talkTitle}
                  talkTitle={talkTitle}
                  talkList={talkList}
                />
              ))}
            <div><p style={{ textAlign: 'right'}}>December 2022</p></div>
            </Talk>
          </TalkMain>
        </PhilosophyMain>
        <CompanyLinks padding={{ pc: '130px 0 0' }} />
      </PhilosophyMainWrapper>
    </>
  )
}

PhilosophyPage.getLayout = function getLayout(page: ReactElement) {
  const pageTitle = {
    title: 'Corporate Information',
    subTitle: '',
  }

  const breadCrumbs = [
    { name: 'Home', path: '/en/' },
    { name: 'Corporate Information', path: '/en/company/' },
    { name: 'Philosophy', path: '' }, 
  ]

  return (
    <TitleLayout {...pageTitle} breadCrumbs={breadCrumbs} isRequiredBackground>
      {page}
    </TitleLayout>
  )
}

const PhilosophyMainWrapper = styled.div`
  width: 1040px;
  padding-bottom: 130px;
  margin: 0 auto 0;

  @media screen and (max-width: 1047px) {
    width: 100%;
    margin: 0 auto 0;
    padding: 0 20px 70px;
  }
`

const PhilosophyMain = styled.div`
  margin-top: 78px;

  @media screen and (max-width: 1047px) {
    margin-top: 40px;
  }
`

const Philosophy = styled.section`
  margin-top: 132px;
  background-color: ${theme.colors.backgroundGrey};
  padding: 70px;

  h3 {
    color: ${theme.colors.trialBlue};
    font-size: 28px;
    font-weight: bold;
    text-align: center;
    line-height: 1.2;
  }

  @media screen and (max-width: 1047px) {
    margin-top: 70px;
    padding: 40px 20px;

    h3 {
      font-size: 20px;
    }
  }
`

const PhilosophyTriangle = styled.div`
  margin-top: 80px;
  display: flex;
  gap: 80px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    align-items: center;
    margin-top: 40px;
    gap: 40px;
  }
`

const ImageWrapper = styled.div`
  width: 440px;
  height: 340px;

  @media screen and (max-width: 1047px) {
    width: 295px;
    height: 228px;

    img {
      width: 100%;
      height: 100%;
    }
  }
`

const Explanation = styled.div``

const SpecialTalk = styled.section`
  margin-top: 130px;

  h3 {
    font-size: 22px;
    color: #3786c7;
    line-height: 32px;
    text-align: center;
    font-family: ${theme.fontEn};
  }

  span {
    display: block;
    font-size: 18px;
    line-height: 1;
    color: ${theme.colors.textTitle};
  }

  img {
    width: 100%;
  }

  @media screen and (max-width: 1047px) {
    margin-top: 65px;

    h3 {
      font-size: 18px;
    }

    span {
      font-size: 12px;
    }
  }
`

const NameWrapper = styled.div`
  margin-top: 46px;
  display: flex;
  justify-content: center;
  gap: 93px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    align-items: center;
    gap: 49px;
  }
`

const Name = styled.div`
  width: 274px;
  height: 64px;
  padding: 17px 25px;
  position: relative;

  p {
    color: #fff;
    font-size: 26px;
    line-height: 1;
    margin-top: -12px; 
  }

  &[data-left] {
    background-color: #3786c7;

    &::after {
      content: '';
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 32px 0 32px 30px;
      border-color: transparent transparent transparent #3786c7;
      display: inline-block;
      position: absolute;
      right: -30px;
      top: 0;
    }

    &::before {
      content: '';
      height: 2px;
      width: 90px;
      background-color: #3786c7;
      transform: rotate(45deg);
      display: inline-block;
      position: absolute;
      top: 31px;
      right: -91px;
    }
  }

  &[data-right] {
    background-color: ${theme.colors.trialBlue};

    &::after {
      content: '';
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 32px 30px 32px 0;
      border-color: transparent ${theme.colors.trialBlue} transparent
        transparent;
      display: inline-block;
      position: absolute;
      left: -30px;
      top: 0;
    }

    &::before {
      content: '';
      height: 2px;
      width: 90px;
      background-color: ${theme.colors.trialBlue};
      transform: rotate(-45deg);
      display: inline-block;
      position: absolute;
      top: 31px;
      left: -91px;
    }
  }

  @media screen and (max-width: 1047px) {
    height: 50px;
    padding: 13px 50px 12px;

    p {
      font-size: 22px;
    }

    &[data-left]::after,
    &[data-right]::after {
      display: none;
    }

    &[data-left]::before {
      width: 35px;
      top: 85px;
      left: 50%;
      transform: rotate(45deg) translateX(-50%);
    }

    &[data-right]::before {
      width: 35px;
      top: -39px;
      left: 50%;
      transform: rotate(-45deg) translateX(-50%);
    }
  }
`

const TalkMain = styled.section``

const TalkLead = styled.div`
  h3 {
    color: ${theme.colors.trialBlue};
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 40px;
  }

  p {
    font-size: 18px;
    line-height: 2;

    & + p {
      margin-top: 30px;
    }
  }

  @media screen and (max-width: 1047px) {
    h3 {
      font-size: 20px;
      margin-bottom: 20px;
    }

    p {
      font-size: 15px;
      line-height: 1.8;

      & + p {
        margin-top: 20px;
      }
    }
  }
`

const Talk = styled.div``
